import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback
} from 'react';
import { useFormik } from 'formik';
import { Dropdown, Button, TextInput } from 'carbon-components-react';
import { Edit16 } from '@carbon/icons-react';
import {
  LocalStoreContext,
  LocalDispatchContext
} from '../../../localStore/context';
import { EnquiryShape } from '../../../localStore/types';
import { setShape } from '../../../localStore/actions';
import { stateListItems } from 'app/contants';
import Utils from 'utilities/helper.util';

const getSelectedItem = (list: any[], val: string | undefined) => {
  return val ? list.find((item) => item.value === val) : undefined;
};

const ShapeDetailForm: React.FC<Props> = ({
  onClose,
  selectedShapeId,
  setShapeDetailFormTouched
}) => {
  const addressLine1InputRef = useRef<HTMLInputElement>(null);
  const [selectedShape, setSelectedShape] = useState<EnquiryShape>();
  const refineAddress = () => {
    addressLine1InputRef.current && addressLine1InputRef.current.focus();
  };
  const localStore = useContext(LocalStoreContext);
  const localDispatch = useContext(LocalDispatchContext);
  const { shapes } = localStore;
  const {
    touched,
    values,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    setValues
  } = useFormik({
    initialValues: {},
    onSubmit: (values: InitialValues) => {
      updateShapeDetails(values);
      onClose();
    }
  });

  const updateShapeDetails = useCallback(
    (values: InitialValues) => {
      if (selectedShape) {
        localDispatch(
          setShape({
            ...selectedShape,
            address: {
              ...selectedShape?.address,
              line1: values.addressLine1 || null,
              locality: values.addressLocality || null,
              postcode: values.addressPostcode || null,
              state: values.addressState || null
            },
            isError:
              !values.addressLine1 ||
              !values.addressLocality ||
              !values.addressState ||
              !values.addressPostcode,
            userReference: values.userReference || undefined
          })
        );
      }
    },
    [selectedShape, localDispatch]
  );

  useEffect(() => {
    if (shapes.length > 0) {
      const selected = shapes.find((s) => s.uid === selectedShapeId);
      setSelectedShape(selected);
    }
  }, [shapes, selectedShapeId]);

  useEffect(() => {
    if (selectedShape) {
      setValues({
        addressLine1: selectedShape.address.line1 || '',
        addressLocality: selectedShape.address.locality || '',
        addressPostcode: selectedShape.address.postcode || '',
        addressState: selectedShape.address.state || '',
        userReference: selectedShape.userReference || ''
      });
    }
  }, [selectedShape, setValues]);

  useEffect(() => {
    const isTouched = !Utils.isObjectEmpty(touched);
    setShapeDetailFormTouched(isTouched);
  }, [touched, setShapeDetailFormTouched]);

  return (
    <div className="shape-detail-form">
      <div className="form-body">
        <div className="form-input-section">
          <div className="address-header">
            <div className="bx--label">Approximate address</div>
          </div>
          <div className="address-body">
            <div className="bx--form-item bx--text-input-wrapper">
              <div className="bx--text-input__field-outer-wrapper">
                <div className="bx--text-input__field-wrapper">
                  <input
                    ref={addressLine1InputRef}
                    id="addressLine1"
                    placeholder="Street"
                    type="text"
                    className="bx--text-input bx--text__input address-line-1"
                    title="Street"
                    name="addressLine1"
                    data-testid="addressLine1"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onInput={() => setFieldTouched('addressLine1')}
                    value={values.addressLine1 || ''}
                  />
                  <Edit16
                    className="bx--date-picker__icon"
                    onClick={() => refineAddress()}
                  />
                </div>
              </div>
            </div>
            {selectedShape?.address?.locality ? (
              <div className="address-body-content static-value">
                {values.addressLocality}
              </div>
            ) : (
              <TextInput
                id="addressLocality"
                name="addressLocality"
                placeholder="Suburb/town"
                hideLabel={true}
                labelText=""
                data-testid="addressLocality"
                onBlur={handleBlur}
                onChange={handleChange}
                onInput={() => setFieldTouched('addressLocality')}
                value={values.addressLocality || ''}
              />
            )}
            <div className="address-body-content state-and-postcode-input">
              {selectedShape?.address?.state ? (
                <div className="state sub-content static-value">
                  {values.addressState}
                </div>
              ) : (
                <Dropdown
                  id="addressState"
                  className="state"
                  items={stateListItems}
                  itemToString={(item: any) => (item ? item.label : '')}
                  label="Choose an option"
                  titleText=""
                  selectedItem={getSelectedItem(
                    stateListItems,
                    values.addressState
                  )}
                  onChange={({ selectedItem }) => {
                    setFieldTouched('addressState');
                    setFieldValue('addressState', selectedItem.value);
                  }}
                />
              )}
              {selectedShape?.address?.postcode ? (
                <div className="postcode sub-content static-value">
                  {values.addressPostcode}
                </div>
              ) : (
                <TextInput
                  id="addressPostcode"
                  name="addressPostcode"
                  placeholder="Postcode"
                  className="postcode"
                  hideLabel={true}
                  labelText=""
                  data-testid="addressPostcode"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  onInput={() => setFieldTouched('addressPostcode')}
                  value={values.addressPostcode || ''}
                />
              )}
            </div>
          </div>
        </div>
        <div className="form-input-section">
          <TextInput
            id="userReference"
            name="userReference"
            labelText="Your reference (optional)"
            placeholder="Brief reference to help identify this job"
            data-testid="userReference"
            maxLength={50}
            onChange={handleChange}
            onBlur={handleBlur}
            onInput={() => setFieldTouched('userReference')}
            value={values.userReference}
          />
        </div>
        <div className="form-input-section">
          <div className="buttons">
            <Button
              id="cancelShapeDetails"
              name="cancelShapeDetails"
              kind="tertiary"
              size="field"
              onClick={() => onClose()}
            >
              Cancel
            </Button>
            <Button
              id="submitShapeDetails"
              name="submitShapeDetails"
              kind="secondary"
              size="field"
              type="submit"
              data-testid="submitShapeDetails"
              onClick={() => handleSubmit()}
              disabled={
                !values.addressLine1 ||
                !values.addressLocality ||
                !values.addressState ||
                !values.addressPostcode ||
                Utils.isObjectEmpty(touched)
              }
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

type InitialValues = {
  addressLine1?: string;
  addressLocality?: string;
  addressPostcode?: string;
  addressState?: string;
  userReference?: string;
};

type Props = {
  onClose: Function;
  selectedShapeId: number;
  setShapeDetailFormTouched: Function;
};

export default React.memo(ShapeDetailForm);
